import { Link, useParams } from "react-router-dom";
import Seo from "@/components/Seo";
import { CTABand } from "@/components/ui-bits";
import { usePost, usePosts } from "@/hooks/useContent";
import { ArrowLeft } from "lucide-react";

export default function BlogPost() {
  const { slug } = useParams();
  const { data: post, error, isLoading } = usePost(slug);
  const { data: posts } = usePosts();
  const others = (posts || []).filter((p) => p.slug !== slug).slice(0, 3);

  if (isLoading) return <div className="container-x py-24"><div className="card h-96 animate-pulse" /></div>;
  if (error || !post) {
    return (
      <div className="container-x py-24 text-center">
        <h1 className="font-serif text-3xl font-semibold text-navy">Insight not found</h1>
        <Link to="/insights" className="btn-gold mt-6">Back to Insights</Link>
      </div>
    );
  }

  const articleJsonld = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.excerpt,
    author: { "@type": "Organization", name: post.author || "Orvella Overseas Private Limited" },
    publisher: { "@type": "Organization", name: "Orvella Overseas Private Limited" },
    datePublished: post.created_at,
    articleSection: post.category,
  };

  return (
    <>
      <Seo
        title={`${post.title} | Orvella Overseas Insights`}
        description={post.excerpt}
        path={`/insights/${post.slug}`}
        image={post.cover_image}
        jsonld={articleJsonld}
      />

      <article className="container-x max-w-3xl py-12 md:py-16" data-testid="insight-article">
        <Link to="/insights" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-gold-dark hover:text-navy">
          <ArrowLeft size={14} /> All Insights
        </Link>
        <span className="mt-6 block text-[11px] font-bold uppercase tracking-[0.18em] text-gold-dark">{post.category}</span>
        <h1 className="mt-3 font-serif text-3xl font-semibold leading-tight text-navy sm:text-4xl">{post.title}</h1>
        <p className="mt-3 text-xs text-mist">By {post.author} · Orvella Overseas Private Limited</p>
        {post.cover_image && (
          <div className="mt-8 overflow-hidden rounded-xl border border-line">
            <img src={post.cover_image} alt={post.title} className="h-auto w-full object-cover" loading="eager" />
          </div>
        )}
        <div
          className="insight-content prose-natural mt-10 space-y-5 text-[15px] leading-relaxed text-ink/90 [&_h2]:mt-8 [&_h2]:font-serif [&_h2]:text-2xl [&_h2]:font-semibold [&_h2]:text-navy [&_p]:leading-relaxed"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />
      </article>

      {others.length > 0 && (
        <section className="section-pad bg-sand">
          <div className="container-x">
            <h2 className="font-serif text-2xl font-semibold text-navy">More Insights</h2>
            <div className="mt-8 grid gap-5 md:grid-cols-3">
              {others.map((p) => (
                <Link key={p.id} to={`/insights/${p.slug}`} className="card card-hover p-6">
                  <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-gold-dark">{p.category}</span>
                  <h3 className="mt-2 font-serif text-lg font-semibold leading-snug text-navy">{p.title}</h3>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <CTABand />
    </>
  );
}
